import React, { useEffect, useMemo, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Tabs, Tab, Button, Form, Row, Col, Card, Alert, Badge, Table } from 'react-bootstrap';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';
import { useExchangeRate } from '../ExchangeRateContext';
import SettingsModal from '../components/SettingsModal';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

// Grouping Styles
const groupingStyles = `
  .group-blue { background-color: #f1f7ff !important; border-left: 3px solid #0d6efd !important; }
  .group-red { background-color: #fff5f5 !important; border-left: 3px solid #ff4d4d !important; }
  
  .group-item { border-top: none !important; }
  .group-last { border-bottom: 1px solid !important; }
  .group-last.group-blue { border-bottom-color: #0d6efd !important; }
  .group-last.group-red { border-bottom-color: #ff4d4d !important; }

  .group-item td { padding-top: 4px !important; padding-bottom: 4px !important; }
  .ps-4 { padding-left: 1.5rem !important; }
`;

// Helper for dates
const todayYMD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const HisobotlarUI = () => (
  <style>{groupingStyles}</style>
);

const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const datePart = d.toLocaleDateString('ru-RU');
  if (!includeTime) return datePart;
  return `${datePart} ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatCurrency = (val, currency = 'UZS') => {
  return Number(val || 0).toLocaleString('ru-RU') + (currency === 'USD' ? ' $' : '');
};

const PAYMENT_TYPES = {
  0: { label: 'Naqd', color: 'success' },
  1: { label: 'Terminal', color: 'warning' },
  2: { label: 'P2P', color: 'info' },
  3: { label: 'Dollar', color: 'primary' }
};

const CUSTOMER_TYPES = {
  1: 'Odatiy',
  2: 'Qarzdor',
  3: 'Ulgurchi'
};

// --- Sub-component: Sales Report (Original Hisobotlar Content) ---
const SalesReportTab = ({ isActive }) => {
  const { token, user } = useAuth();

  // Filters
  const [startDate, setStartDate] = useState(todayYMD());
  const [endDate, setEndDate] = useState(todayYMD());
  const [storeId, setStoreId] = useState('');
  const [staffId, setStaffId] = useState('');

  // Data
  const [stores, setStores] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [rows, setRows] = useState([]);
  const [usdRows, setUsdRows] = useState([]);
  const [refundRows, setRefundRows] = useState([]);
  const [c1Payments, setC1Payments] = useState([]);
  const [c2Payments, setC2Payments] = useState([]);
  const [c3Payments, setC3Payments] = useState([]);
  const [typeCounts, setTypeCounts] = useState({ customer1: 0, customer2: 0, customer3: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isStaff = user?.role === 'staff';
  const ownerId = user?.ownerId || user?.id || null;

  // Fetch stores
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    const url = isStaff ? '/api/store-permissions/view-stores' : '/api/stores';
    fetch(url, {
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(ownerId ? { 'x-owner-id': ownerId } : {}),
        ...(isStaff && user?.id ? { 'x-staff-id': user.id } : {})
      }
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to fetch stores: ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
        setStores(arr);
        if (isStaff && !storeId && arr.length > 0) {
          const firstId = arr[0].id || arr[0].ID;
          if (firstId) setStoreId(String(firstId));
        }
      })
      .catch((e) => {
        if (e.name !== 'AbortError') console.error(e);
      });
    return () => controller.abort();
  }, [token, isStaff, ownerId]);

  // Fetch staffs
  useEffect(() => {
    if (!token || isStaff) return;
    const controller = new AbortController();
    fetch('/api/staffs', {
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(ownerId ? { 'x-owner-id': ownerId } : {})
      }
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to fetch staffs: ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];
        setStaffs(arr);
      })
      .catch((e) => {
        if (e.name !== 'AbortError') console.error(e);
      });
    return () => controller.abort();
  }, [token, ownerId, isStaff]);

  const fetchReport = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ startDate, endDate });
      if (storeId) qs.set('storeId', storeId);
      if (!isStaff && staffId) qs.set('staffId', staffId);

      const qsUsd = new URLSearchParams(qs.toString());
      qsUsd.set('currency', 'USD');

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(ownerId ? { 'x-owner-id': ownerId } : {}),
        ...(isStaff && user?.id ? { 'x-staff-id': user.id } : {})
      };

      const [resAll, resUsd, resRefunds, resTypeCounts, resC1, resC2, resC3] = await Promise.all([
        fetch(`/api/reports/sales-by-product?${qs.toString()}`, { headers }),
        fetch(`/api/reports/sales-by-product?${qsUsd.toString()}`, { headers }),
        fetch(`/api/reports/refunds?${qs.toString()}`, { headers }),
        fetch(`/api/reports/sales-customer-type-counts?${qs.toString()}`, { headers }),
        fetch(`/api/reports/customer1-payments?${qs.toString()}`, { headers }),
        fetch(`/api/reports/customer2-payments?${qs.toString()}`, { headers }),
        fetch(`/api/reports/customer3-payments?${qs.toString()}`, { headers })
      ]);

      if (!resAll.ok) throw new Error(`Report request failed: ${resAll.status}`);
      // ... check others ...

      const [dataAll, dataUsd, dataRefunds, dataTypeCounts, dataC1, dataC2, dataC3] = await Promise.all([
        resAll.json(), resUsd.json(), resRefunds.json(), resTypeCounts.json(), resC1.json(), resC2.json(), resC3.json()
      ]);

      setRows(Array.isArray(dataAll) ? dataAll : []);
      setUsdRows(Array.isArray(dataUsd) ? dataUsd : []);
      setRefundRows(Array.isArray(dataRefunds) ? dataRefunds : []);
      setC1Payments(Array.isArray(dataC1) ? dataC1 : []);
      setC2Payments(Array.isArray(dataC2) ? dataC2 : []);
      setC3Payments(Array.isArray(dataC3) ? dataC3 : []);

      if (dataTypeCounts && typeof dataTypeCounts === 'object') {
        setTypeCounts({
          customer1: Number(dataTypeCounts.customer1) || 0,
          customer2: Number(dataTypeCounts.customer2) || 0,
          customer3: Number(dataTypeCounts.customer3) || 0,
        });
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate, storeId, staffId, isStaff, ownerId, user]);

  useEffect(() => {
    if (isActive) fetchReport();
  }, [isActive, fetchReport]);

  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const pushBlock = (aoa, title, header, rows, footerRows = []) => {
        if (aoa.length) aoa.push([]);
        if (title) aoa.push([title]);
        if (header && header.length) aoa.push(header);
        if (Array.isArray(rows) && rows.length) aoa.push(...rows);
        if (Array.isArray(footerRows) && footerRows.length) aoa.push(...footerRows);
      };

      const salesRows = rows.map((r, idx) => [idx + 1, r.product_name, Number(r.total_quantity), Number(r.total_price)]);
      const usdSalesRows = usdRows.map((r, idx) => [idx + 1, r.product_name, Number(r.total_quantity), Number(r.total_price)]);
      const refunds = refundRows.map((r, idx) => [idx + 1, r.product_name, r.customer_name, Number(r.qty), Number(r.price), Number(r.total_price), r.currency, r.doer_name, r.store_name]);

      // Pivot c1
      const c1Pivot = {};
      c1Payments.forEach(r => {
        const cur = (r.currency || 'UZS').toUpperCase();
        const method = (r.payment_method || '').toLowerCase();
        if (!c1Pivot[cur]) c1Pivot[cur] = { cash: 0, card: 0, terminal: 0, total: 0 };
        const amt = Number(r.total_amount || 0);
        if (method.includes('cash')) c1Pivot[cur].cash += amt;
        else if (method.includes('card')) c1Pivot[cur].card += amt;
        else if (method.includes('terminal')) c1Pivot[cur].terminal += amt;
        c1Pivot[cur].total += amt;
      });
      const c1R = Object.keys(c1Pivot).map(c => [c, c1Pivot[c].cash, c1Pivot[c].card, c1Pivot[c].terminal, c1Pivot[c].total]);

      const c2 = c2Payments.map(p => [p.customer_name, Number(p.amount)]);
      const c3 = c3Payments.map(p => [p.customer_name, Number(p.amount), p.currency]);

      const all = [];
      pushBlock(all, "Odatiy mijozlar to'lovlari:", ['Valyuta', 'Naqd', 'Karta', 'Terminal', 'Jami'], c1R);
      pushBlock(all, "Qarz mijozlar:", ['Mijoz', 'Miqdor'], c2);
      pushBlock(all, "Ulgurchi mijozlar:", ['Mijoz', 'Miqdor', 'Valyuta'], c3);
      pushBlock(all, "Sotuvlar (UZS):", ['#', 'Nomi', 'Soni', 'Summa'], salesRows);
      pushBlock(all, "Sotuvlar (USD):", ['#', 'Nomi', 'Soni', 'Summa'], usdSalesRows);
      pushBlock(all, "Qaytganlar:", ['#', 'Nomi', 'Mijoz', 'Soni', 'Narx', 'Jami', 'Val', 'Xodim', 'Ombor'], refunds);

      const ws = XLSX.utils.aoa_to_sheet(all);
      XLSX.utils.book_append_sheet(wb, ws, 'Hisobot');
      XLSX.writeFile(wb, `Hisobot_${startDate}_${endDate}.xlsx`);
    } catch (e) {
      console.error(e);
      toast.error('Export error');
    }
  };

  const totalPrice = useMemo(() => rows.reduce((a, r) => a + (Number(r.total_price) || 0), 0), [rows]);
  const totalPriceUSD = useMemo(() => usdRows.reduce((a, r) => a + (Number(r.total_price) || 0), 0), [usdRows]);

  return (
    <div className="py-3">
      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label>Boshlanish sani</Form.Label>
              <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>Tugash sana</Form.Label>
              <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>Ombor</Form.Label>
              <Form.Select value={storeId} onChange={e => setStoreId(e.target.value)}>
                {!isStaff && <option value="">Barchasi</option>}
                {stores.map(s => <option key={s.id || s.ID} value={s.id || s.ID}>{s.name || s.store_name}</option>)}
              </Form.Select>
            </Col>
            {!isStaff && (
              <Col md={3}>
                <Form.Label>Xodim</Form.Label>
                <Form.Select value={staffId} onChange={e => setStaffId(e.target.value)}>
                  <option value="">Barchasi</option>
                  {staffs.map(s => <option key={s.id} value={s.id}>{s.full_name || s.username}</option>)}
                </Form.Select>
              </Col>
            )}
            <Col md={3}>
              <Button className="w-100" onClick={fetchReport} disabled={loading}>Filtrni qo'llash</Button>
            </Col>
            <Col md={3}>
              <Button variant="success" className="w-100" onClick={handleExportExcel} disabled={loading}>Excelga yuklash</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Counts */}
      <div className="d-flex gap-2 mb-3">
        <Badge bg="success" className="p-2">Odatiy: {typeCounts.customer1}</Badge>
        <Badge bg="success" className="p-2">Qarz: {typeCounts.customer2}</Badge>
        <Badge bg="success" className="p-2">Ulgurchi: {typeCounts.customer3}</Badge>
      </div>

      {loading && <Loader />}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Tables - Simplified render for brevity, assuming standard rendering */}
      {/* Customer 1 */}
      <Card className="mb-3">
        <Card.Body>
          <h5>Odatiy mijozlar to'lovlari</h5>
          <div className="table-responsive">
            <Table bordered>
              <thead><tr><th>Valyuta</th><th>Naqd</th><th>Karta</th><th>Terminal</th><th>Jami</th></tr></thead>
              <tbody>
                {/* Logic to render c1Payments */}
                {(() => {
                  const byCurrency = {};
                  c1Payments.forEach(r => {
                    const cur = r.currency || 'UZS';
                    const pm = (r.payment_method || '').toLowerCase();
                    const amt = Number(r.total_amount || 0);
                    if (!byCurrency[cur]) byCurrency[cur] = { cash: 0, card: 0, terminal: 0 };
                    if (pm.includes('cash')) byCurrency[cur].cash += amt;
                    else if (pm.includes('card')) byCurrency[cur].card += amt;
                    else if (pm.includes('terminal')) byCurrency[cur].terminal += amt;
                  });
                  const keys = Object.keys(byCurrency);
                  if (!keys.length) return <tr><td colSpan={5} className="text-center text-muted">Ma'lumot yo'q</td></tr>;
                  return keys.map(k => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td>{formatCurrency(byCurrency[k].cash)}</td>
                      <td>{formatCurrency(byCurrency[k].card)}</td>
                      <td>{formatCurrency(byCurrency[k].terminal)}</td>
                      <td>{formatCurrency(byCurrency[k].cash + byCurrency[k].card + byCurrency[k].terminal)}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between"><h5>UZS bo'yicha sotuvlar</h5> <small>Jami: {formatCurrency(totalPrice)}</small></div>
          <Table striped hover>
            <thead><tr><th>#</th><th>Mahsulot</th><th>Soni</th><th>Summa</th></tr></thead>
            <tbody>
              {rows.map((r, i) => <tr key={i}><td>{i + 1}</td><td>{r.product_name}</td><td>{r.total_quantity}</td><td>{formatCurrency(r.total_price)}</td></tr>)}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between"><h5>USD bo'yicha sotuvlar</h5> <small>Jami: {formatQuantity(totalPriceUSD)}</small></div>
          <Table striped hover>
            <thead><tr><th>#</th><th>Mahsulot</th><th>Soni</th><th>Summa (USD)</th></tr></thead>
            <tbody>
              {usdRows.map((r, i) => <tr key={i}><td>{i + 1}</td><td>{r.product_name}</td><td>{r.total_quantity}</td><td>{formatCurrency(r.total_price)}</td></tr>)}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h5>Qaytarilganlar</h5>
          <Table>
            <thead><tr><th>#</th><th>Mahsulot</th><th>Mijoz</th><th>Soni</th><th>Narx</th><th>Jami</th><th>Val</th><th>Xodim</th></tr></thead>
            <tbody>
              {refundRows.map((r, i) => (
                <tr key={i} className="text-danger">
                  <td>{i + 1}</td><td>{r.product_name}</td><td>{r.customer_name}</td><td>{r.qty}</td><td>{r.price}</td><td>{r.total_price}</td><td>{r.currency}</td><td>{r.doer_name}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

// --- Sub-component: History Tab ---
const HistoryTab = ({ isActive, staffs }) => {
  const { token } = useAuth();
  const { rate: exchangeRate } = useExchangeRate();

  // Filters
  const [startDate, setStartDate] = useState(todayYMD());
  const [endDate, setEndDate] = useState(todayYMD());
  const [customerType, setCustomerType] = useState('');
  const [type, setType] = useState('');
  const [staffId, setStaffId] = useState('');
  const [action, setAction] = useState('true'); // Default to Payments only

  // Data
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Chart configuration
  const [chartConfig, setChartConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('history_chart_config');
      return saved ? JSON.parse(saved) : { 0: true, 1: true, 2: true, 3: true };
    } catch { return { 0: true, 1: true, 2: true, 3: true }; }
  });

  useEffect(() => {
    localStorage.setItem('history_chart_config', JSON.stringify(chartConfig));
  }, [chartConfig]);

  // Fetch Logic
  const fetchTransactions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        startDate,
        endDate,
        ...(customerType && { customer_type: customerType }),
        ...(type && { type }),
        action: 'true', // Force payments only for this tab as per request 4
        ...(staffId && { staffId })
      });

      const res = await fetch(`/api/payment-transactions?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch transactions');
      const data = await res.json();
      setTransactions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Tarixni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate, customerType, type, staffId]); // removed action from dependencies as it is fixed to 'true'

  useEffect(() => {
    if (isActive) fetchTransactions();
  }, [isActive, fetchTransactions]);

  const chartData = useMemo(() => {
    const sums = { 0: 0, 1: 0, 2: 0, 3: 0 };
    transactions.forEach(t => {
      // payment transactions (action=true) are already filtered by backend
      if (chartConfig[t.type]) {
        sums[t.type] += Number(t.amount);
      }
    });

    // Color mapping
    const bgColors = Object.keys(sums).filter(k => chartConfig[k]).map(k => {
      const variant = PAYMENT_TYPES[k].color;
      if (variant === 'success') return '#198754';
      if (variant === 'warning') return '#ffc107';
      if (variant === 'info') return '#0dcaf0';
      return '#0d6efd';
    });

    return {
      labels: Object.keys(sums).filter(k => chartConfig[k]).map(k => PAYMENT_TYPES[k].label),
      datasets: [{
        data: Object.keys(sums).filter(k => chartConfig[k]).map(k => sums[k]),
        backgroundColor: bgColors,
      }]
    };
  }, [transactions, chartConfig]);

  const overallTotal = useMemo(() => {
    let total = 0;
    Object.keys(PAYMENT_TYPES).forEach(k => {
      if (chartConfig[k]) {
        const sum = transactions.filter(t => t.type == k).reduce((a, b) => a + Number(b.amount), 0);
        if (k == 3 && exchangeRate) {
          total += sum * exchangeRate;
        } else {
          total += sum;
        }
      }
    });
    return total;
  }, [transactions, chartConfig, exchangeRate]);

  // Pagination Logic
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return transactions.slice(start, start + itemsPerPage);
  }, [transactions, currentPage, itemsPerPage]);

  return (
    <div className="pt-3">
      {/* Filters */}
      <Row className="g-3 mb-4">
        <Col md={2}>
          <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Select value={type} onChange={e => setType(e.target.value)}>
            <option value="">Barcha turlar</option>
            <option value="0">Naqd</option>
            <option value="1">Terminal</option>
            <option value="2">P2P</option>
            <option value="3">Dollar</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select value={customerType} onChange={e => setCustomerType(e.target.value)}>
            <option value="">Barcha mijozlar</option>
            <option value="1">Odatiy</option>
            <option value="2">Qarzdor</option>
            <option value="3">Ulgurchi</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button onClick={fetchTransactions} variant="primary" className="w-100">Filtrlash</Button>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4} className="d-flex flex-column align-items-center justify-content-center border-end">
          {/* Totals by type (Text) */}
          <div className="w-100">
            <h6>Jami summalar:</h6>
            {Object.entries(PAYMENT_TYPES).map(([k, v]) => {
              const sum = transactions.filter(t => t.type == k).reduce((a, b) => a + Number(b.amount), 0);
              return (
                <div key={k} className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Check
                    type="checkbox"
                    checked={!!chartConfig[k]}
                    onChange={(e) => setChartConfig(prev => ({ ...prev, [k]: e.target.checked }))}
                    label={v.label}
                  />
                  <span className={`text-${v.color} fw-bold`}>{formatCurrency(sum, k == 3 ? 'USD' : 'UZS')}</span>
                </div>
              )
            })}
          </div>
        </Col>
        <Col md={8}>
          <div className="d-flex align-items-center">
            <div style={{ height: '200px', width: '300px', display: 'flex', justifyContent: 'center' }}>
              <Pie data={chartData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />
            </div>
            <div className="ms-4">
              <h3 className="text-secondary mb-0">Umumiy:</h3>
              <h2 className="text-primary fw-bold mb-0">{formatCurrency(overallTotal)}</h2>
              {exchangeRate && <small className="text-muted">Kurs: {exchangeRate}</small>}
            </div>
          </div>
        </Col>
      </Row>

      {loading ? <Loader /> : (
        <>
          <div className="table-responsive">
            <Table bordered hover>
              <thead>
                <tr>
                  <th>Sana</th>
                  <th>Mijoz turi</th>
                  <th>Harakat</th>
                  <th>To'lov turi</th>
                  <th>Miqdor</th>
                  <th>Bajaruvchi</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((t, idx) => {
                  const prev = idx > 0 ? paginatedData[idx - 1] : null;
                  const next = idx < paginatedData.length - 1 ? paginatedData[idx + 1] : null;

                  const isGroupedWithPrev = prev && Math.abs(new Date(t.created_at) - new Date(prev.created_at)) < 5000;
                  const isGroupedWithNext = next && Math.abs(new Date(next.created_at) - new Date(t.created_at)) < 5000;

                  const isInGroup = isGroupedWithPrev || isGroupedWithNext;
                  const isItem = isGroupedWithPrev;
                  const isLast = isInGroup && !isGroupedWithNext;

                  let rowClass = isInGroup ? "group-blue" : "";
                  if (isItem) rowClass += " group-item";
                  if (isLast) rowClass += " group-last";

                  return (
                    <tr key={t.id} className={rowClass}>
                      <td className={isItem ? 'text-muted ps-4' : ''}>
                        {!isItem ? formatDate(t.created_at, true) : ''}
                      </td>
                      <td>{CUSTOMER_TYPES[t.customer_type]}</td>
                      <td>
                        <Badge bg="success">To'lov</Badge>
                      </td>
                      <td>
                        <Badge bg={PAYMENT_TYPES[t.type].color}>{PAYMENT_TYPES[t.type].label}</Badge>
                      </td>
                      <td className="fw-bold">{formatCurrency(t.amount, t.type == 3 ? 'USD' : 'UZS')}</td>
                      <td>{t.staff_name || 'EGA'}</td>
                    </tr>
                  )
                })}
                {paginatedData.length === 0 && <tr><td colSpan={6} className="text-center">Ma'lumot yo'q</td></tr>}
              </tbody>
            </Table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={transactions.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}
    </div>
  );
};

// --- Sub-component: Withdrawal Tab ---
const WithdrawalTab = ({ isActive, fetchBalance, balance }) => {
  const { token } = useAuth();
  const [amounts, setAmounts] = useState({ 0: '', 1: '', 2: '', 3: '' });
  const [useMax, setUseMax] = useState(false);
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [startDate, setStartDate] = useState(todayYMD());
  const [endDate, setEndDate] = useState(todayYMD());

  const handlePasteMax = (checked) => {
    setUseMax(checked);
    if (checked) {
      setAmounts({ 0: balance[0] || '', 1: balance[1] || '', 2: balance[2] || '', 3: balance[3] || '' });
    } else {
      setAmounts({ 0: '', 1: '', 2: '', 3: '' });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const requests = [];
      // Iterate types 0..3
      for (let i = 0; i <= 3; i++) {
        const val = Number(amounts[i]);
        if (val > 0) {
          requests.push(fetch('/api/payment-transactions', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_type: 1,
              action: false, // Withdrawal
              type: i,
              amount: val
            })
          }));
        }
      }

      if (requests.length === 0) {
        toast.warning("Miqdor kiritilmadi");
        return;
      }

      const responses = await Promise.all(requests);
      let successCount = 0;
      for (const res of responses) {
        if (res.ok) successCount++;
        else {
          const d = await res.json();
          toast.error(d.error || 'Xatolik');
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} ta chiqim bajarildi`);
        setAmounts({ 0: '', 1: '', 2: '', 3: '' });
        setUseMax(false);
        fetchBalance();
        fetchWithdrawals();
      }

    } catch (e) {
      console.error(e);
      toast.error("Xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawals = useCallback(async () => {
    if (!token) return;
    try {
      const qs = new URLSearchParams({ startDate, endDate, action: 'false' });
      const res = await fetch(`/api/payment-transactions?${qs.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setWithdrawals(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, [startDate, endDate, token]);

  useEffect(() => {
    if (isActive) fetchWithdrawals();
  }, [isActive, fetchWithdrawals]);

  return (
    <div className="pt-3">
      <Card className="mb-4">
        <Card.Header>Chiqim qilish</Card.Header>
        <Card.Body>
          <Form.Check
            type="checkbox"
            label="Mavjud balansni ishlatish"
            checked={useMax}
            onChange={e => handlePasteMax(e.target.checked)}
            className="mb-3 fw-bold text-primary"
          />
          <Row className="g-3">
            {[0, 1, 2, 3].map(t => (
              <Col md={3} key={t}>
                <Form.Label>{PAYMENT_TYPES[t].label} (Mavjud: {formatCurrency(balance[t] || 0, t == 3 ? 'USD' : 'UZS')})</Form.Label>
                <Form.Control
                  type="number"
                  value={amounts[t]}
                  onChange={e => {
                    setUseMax(false);
                    setAmounts(p => ({ ...p, [t]: e.target.value }))
                  }}
                  placeholder="0"
                  min="0"
                />
              </Col>
            ))}
          </Row>
          <div className="mt-3 text-end">
            <Button variant="danger" disabled={loading} onClick={handleSubmit}>Chiqim qilish</Button>
          </div>
        </Card.Body>
      </Card>

      <h5>Chiqimlar tarixi</h5>
      <div className="d-flex gap-2 mb-3">
        <Form.Control type="date" style={{ width: 'auto' }} value={startDate} onChange={e => setStartDate(e.target.value)} />
        <Form.Control type="date" style={{ width: 'auto' }} value={endDate} onChange={e => setEndDate(e.target.value)} />
        <Button onClick={fetchWithdrawals}>Filtrlash</Button>
      </div>

      <div className="table-responsive">
        <Table bordered hover>
          <thead>
            <tr>
              <th>Sana</th>
              <th>To'lov turi</th>
              <th>Miqdor</th>
              <th>Bajaruvchi</th>
            </tr>
          </thead>
          <tbody>
            {withdrawals.map((w, idx) => {
              const prev = idx > 0 ? withdrawals[idx - 1] : null;
              const next = idx < withdrawals.length - 1 ? withdrawals[idx + 1] : null;

              const isGroupedWithPrev = prev && Math.abs(new Date(w.created_at) - new Date(prev.created_at)) < 5000;
              const isGroupedWithNext = next && Math.abs(new Date(next.created_at) - new Date(w.created_at)) < 5000;

              const isInGroup = isGroupedWithPrev || isGroupedWithNext;
              const isItem = isGroupedWithPrev;
              const isLast = isInGroup && !isGroupedWithNext;

              let rowClass = isInGroup ? "group-red" : "";
              if (isItem) rowClass += " group-item";
              if (isLast) rowClass += " group-last";

              return (
                <tr key={w.id} className={rowClass}>
                  <td className={isItem ? 'text-muted ps-4' : ''}>
                    {!isItem ? formatDate(w.created_at, true) : ''}
                  </td>
                  <td><Badge bg={PAYMENT_TYPES[w.type].color}>{PAYMENT_TYPES[w.type].label}</Badge></td>
                  <td className="text-danger fw-bold">-{formatCurrency(w.amount, w.type == 3 ? 'USD' : 'UZS')}</td>
                  <td>{w.staff_name || 'EGA'}</td>
                </tr>
              );
            })}
            {!withdrawals.length && <tr><td colSpan={4} className="text-center">Malumot yo'q</td></tr>}
          </tbody>
        </Table>
      </div>
    </div>
  )
};

// --- Main Component ---
const Hisobotlar = () => {
  const { token, user } = useAuth();
  const { rate: exchangeRate } = useExchangeRate();
  const [activeTab, setActiveTab] = useState('history'); // Default to History tab
  const [balance, setBalance] = useState({ 0: 0, 1: 0, 2: 0, 3: 0 });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState('');
  const [staffs, setStaffs] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Balance Config
  const [balanceConfig, setBalanceConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('balance_display_config');
      return saved ? JSON.parse(saved) : { 0: true, 1: true, 2: true, 3: true };
    } catch { return { 0: true, 1: true, 2: true, 3: true }; }
  });

  useEffect(() => {
    localStorage.setItem('balance_display_config', JSON.stringify(balanceConfig));
  }, [balanceConfig]);

  // Fetch Balance
  const fetchBalance = useCallback(async () => {
    if (!token) return;
    setBalanceLoading(true);
    setBalanceError('');
    try {
      const res = await fetch('/api/payment-transactions/balance', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const b = { 0: 0, 1: 0, 2: 0, 3: 0 };
        if (Array.isArray(data)) {
          data.forEach(item => { b[item.type] = Number(item.amount); });
        }
        setBalance(b);
      } else {
        const errData = await res.json();
        setBalanceError(errData.error || 'Balansni yuklab bo\'lmadi');
      }
    } catch (e) {
      console.error('Balance error', e);
      setBalanceError('Server bilan bog\'lanishda xatolik');
    } finally {
      setBalanceLoading(false);
    }
  }, [token]);

  // Combined balance calculation
  const overallBalance = useMemo(() => {
    let sum = 0;
    [0, 1, 2, 3].forEach(t => {
      if (balanceConfig[t]) {
        const val = balance[t] || 0;
        if (t === 3 && exchangeRate) sum += val * exchangeRate;
        else sum += val;
      }
    });
    return sum;
  }, [balance, exchangeRate, balanceConfig]);

  useEffect(() => {
    if (token && (user?.role === 'owner')) {
      fetch('/api/staffs', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setStaffs(Array.isArray(d) ? d : []))
        .catch(console.error);
    }
  }, [token, user]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance, activeTab]);

  return (
    <div className="container-fluid py-3">
      <style>{groupingStyles}</style>
      <SettingsModal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} />

      {/* Top Balance Section */}
      <Card className="mb-4 shadow-sm border-0 bg-light">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-secondary mb-0"><i className="bi bi-wallet2 me-2"></i>Joriy Balans</h5>
            <div className="d-flex align-items-center gap-2">
              {balanceLoading && <div className="spinner-border spinner-border-sm text-primary" role="status"></div>}
              {user?.role === 'owner' && (
                <Button variant="link" className="text-secondary p-0" onClick={() => setShowSettingsModal(true)}>
                  <i className="bi bi-gear-fill fs-5"></i>
                </Button>
              )}
            </div>
          </div>
          {balanceError && <Alert variant="danger" className="py-2 small">{balanceError}</Alert>}
          <Row className="text-center g-3">
            {/* 1. Overall Balance Card */}
            <Col xs={12} md={3}>
              <div className="p-3 rounded text-white shadow-sm h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: '#2c3e50' }}>
                <div className="text-white-50 small text-uppercase fw-bold mb-1">UMUMIY BALANS (UZS)</div>
                <div className="h3 mb-0 fw-bold">{formatCurrency(overallBalance)}</div>
                {exchangeRate && <small className="text-white-50 mt-1">1$ = {exchangeRate} UZS</small>}
              </div>
            </Col>

            {/* 2. Individual Balances with Checkboxes */}
            {[0, 1, 2, 3].map(t => (
              <Col key={t} xs={6} md={2}>
                <div className="p-3 rounded bg-white shadow-sm border-start border-4 h-100 position-relative" style={{ borderColor: `var(--bs-${PAYMENT_TYPES[t].color}) !important` }}>
                  <div className="position-absolute top-0 end-0 p-2">
                    <Form.Check
                      type="checkbox"
                      checked={!!balanceConfig[t]}
                      onChange={(e) => setBalanceConfig(prev => ({ ...prev, [t]: e.target.checked }))}
                      aria-label={`Include ${PAYMENT_TYPES[t].label} in total`}
                    />
                  </div>
                  <div className="text-muted small text-uppercase fw-bold mb-1">{PAYMENT_TYPES[t].label}</div>
                  <div className="h4 mb-0 fw-bold text-dark">{formatCurrency(balance[t], t == 3 ? 'USD' : 'UZS')}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="mb-3 custom-tabs">
        <Tab eventKey="sales" title="Sotuvlar hisoboti">
          <SalesReportTab isActive={activeTab === 'sales'} />
        </Tab>
        <Tab eventKey="history" title="To'lovlar tarixi">
          <HistoryTab isActive={activeTab === 'history'} staffs={staffs} />
        </Tab>
        <Tab eventKey="withdrawal" title="Chiqim">
          <WithdrawalTab isActive={activeTab === 'withdrawal'} fetchBalance={fetchBalance} balance={balance} />
        </Tab>
      </Tabs>
    </div>
  );
};

export default Hisobotlar;

function formatQuantity(val) {
  return Number(val || 0).toLocaleString('ru-RU');
}